import { useMemo } from "react";
import { NccnIcdConfig } from "../../../../../data-model/nccnIcdConfig";
import { NccnRegimenConfig } from "../../../../../data-model/nccnRegimenConfig";
import { DxCodeConfigTab } from "../../../../../enums/dxCodeConfigTab";
import {
  transformDrugTableData,
  transformRegimenTableData,
} from "../utils/tableDataTransformers";

interface UseTableDataReturn {
  drugTableData: ReturnType<typeof transformDrugTableData>;
  regimenTableData: ReturnType<typeof transformRegimenTableData>;
  filteredDrugTableData: ReturnType<typeof transformDrugTableData>;
  filteredRegimenTableData: ReturnType<typeof transformRegimenTableData>;
  tableData:
    | ReturnType<typeof transformDrugTableData>
    | ReturnType<typeof transformRegimenTableData>;
}

/**
 * Hook to transform and filter table data based on active tab and search term
 */
export const useTableData = (
  activeTab: DxCodeConfigTab,
  nccnIcdConfigData: NccnIcdConfig[],
  nccnRegimenConfigData: NccnRegimenConfig[],
  searchTerm: string,
): UseTableDataReturn => {
  // Transform drug table data
  const drugTableData = useMemo(
    () => transformDrugTableData(nccnIcdConfigData),
    [nccnIcdConfigData],
  );

  // Transform regimen table data
  const regimenTableData = useMemo(
    () => transformRegimenTableData(nccnRegimenConfigData),
    [nccnRegimenConfigData],
  );

  // Filter drug table data based on search term
  const filteredDrugTableData = useMemo(() => {
    if (!searchTerm.trim()) {
      return drugTableData;
    }
    const lowerSearchTerm = searchTerm.toLowerCase();
    return drugTableData.filter(
      (drug) =>
        drug.jCode?.toLowerCase().includes(lowerSearchTerm) ||
        drug.drugName?.toLowerCase().includes(lowerSearchTerm),
    );
  }, [drugTableData, searchTerm]);

  // Filter regimen table data based on search term
  const filteredRegimenTableData = useMemo(() => {
    if (!searchTerm.trim()) {
      return regimenTableData;
    }
    const lowerSearchTerm = searchTerm.toLowerCase();
    return regimenTableData.filter(
      (regimen) =>
        regimen.regimenCode?.toLowerCase().includes(lowerSearchTerm) ||
        regimen.regimenName?.toLowerCase().includes(lowerSearchTerm),
    );
  }, [regimenTableData, searchTerm]);

  // Get table data based on active tab
  const tableData = useMemo(() => {
    return activeTab === DxCodeConfigTab.REGIMEN
      ? filteredRegimenTableData
      : filteredDrugTableData;
  }, [activeTab, filteredRegimenTableData, filteredDrugTableData]);

  return {
    drugTableData,
    regimenTableData,
    filteredDrugTableData,
    filteredRegimenTableData,
    tableData,
  };
};
